import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import './SocialPhotographyModal.css';

function SocialPhotographyModal({ isOpen, onClose, onInventoryChanged }) {
  const { currentUser, canPerformAction } = useAuth();
  const canManage = canPerformAction('inventory.adjustStock');
  const [sku, setSku] = useState('');
  const [pieces, setPieces] = useState([]);
  const [sentPieces, setSentPieces] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [loadingSent, setLoadingSent] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchedSku, setSearchedSku] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const request = useCallback(async (path, options = {}) => {
    const token = await currentUser.getIdToken();
    const response = await fetch(path, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.body ? { 'Content-Type': 'application/json' } : {}) },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success !== true) throw new Error(data.message || 'Request failed.');
    return data;
  }, [currentUser]);

  const loadSent = useCallback(async () => {
    setLoadingSent(true);
    setError('');
    try {
      const data = await request('/api/v1/inventory/social-photography');
      setSentPieces(Array.isArray(data.pieces) ? data.pieces : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSent(false);
    }
  }, [request]);

  useEffect(() => {
    if (isOpen && currentUser) loadSent();
  }, [isOpen, currentUser, loadSent]);

  const search = async (event) => {
    event.preventDefault();
    const nextSku = sku.trim().toUpperCase();
    setError('');
    setMessage('');
    setPieces([]);
    setSearchedSku('');
    if (!nextSku) return;
    setLoadingSearch(true);
    try {
      const data = await request(`/api/v1/inventory/pieces?sku=${encodeURIComponent(nextSku)}`);
      setPieces((Array.isArray(data.pieces) ? data.pieces : []).filter((piece) =>
        piece.status === 'active' && Number(piece.length) > 0));
      setSearchedSku(nextSku);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSearch(false);
    }
  };

  const changeStatus = async (piece, action) => {
    const key = `${piece.sku}_${piece.pieceId}`;
    setBusy(key);
    setError('');
    setMessage('');
    try {
      const data = await request(`/api/v1/inventory/social-photography/${action}`, {
        method: 'POST',
        body: JSON.stringify({ sku: piece.sku, pieceId: piece.pieceId, ...(action === 'send' ? { note } : {}) }),
      });
      setMessage(data.message);
      if (action === 'send') {
        setPieces((previous) => previous.filter((item) => item.pieceId !== piece.pieceId));
        setNote('');
      }
      await loadSent();
      if (action === 'receive' && searchedSku === piece.sku) {
        const refreshed = await request(`/api/v1/inventory/pieces?sku=${encodeURIComponent(searchedSku)}`);
        setPieces((Array.isArray(refreshed.pieces) ? refreshed.pieces : []).filter((item) =>
          item.status === 'active' && Number(item.length) > 0));
      }
      onInventoryChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const formatDate = (value) => {
    const date = value?._seconds ? new Date(value._seconds * 1000) : new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} contentClassName="social-photography-modal">
      <h2>Social Photography</h2>
      <p>Send a whole fabric piece out temporarily. Pieces listed below are excluded from available stock.</p>
      {error && <p className="social-photography-error" role="alert">{error}</p>}
      {message && <p className="social-photography-success" role="status">{message}</p>}

      {canManage && (
        <section className="social-photography-section">
          <h3>Send a piece</h3>
          <form onSubmit={search} className="social-photography-search">
            <label htmlFor="social-photography-sku">Search SKU</label>
            <input id="social-photography-sku" value={sku} onChange={(e) => { setSku(e.target.value); setPieces([]); setSearchedSku(''); }} placeholder="Enter exact SKU" />
            <button type="submit" disabled={loadingSearch || !sku.trim()}>{loadingSearch ? 'Searching…' : 'Search'}</button>
          </form>
          {searchedSku && pieces.length === 0 && <p>No available pieces for {searchedSku}.</p>}
          {pieces.length > 0 && (
            <>
              <label htmlFor="social-photography-note">Note (optional)</label>
              <input id="social-photography-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Who took it or expected return" maxLength={500} />
              <div className="social-photography-table-wrap">
                <table className="inventory-table">
                  <thead><tr><th>SKU</th><th>Piece ID</th><th>Length</th><th>Action</th></tr></thead>
                  <tbody>{pieces.map((piece) => (
                    <tr key={`${piece.sku}_${piece.pieceId}`}>
                      <td>{piece.sku}</td><td>{piece.pieceId}</td><td>{Number(piece.length).toFixed(2)}m</td>
                      <td><button type="button" disabled={!!busy} onClick={() => changeStatus(piece, 'send')}>
                        {busy === `${piece.sku}_${piece.pieceId}` ? 'Sending…' : 'Send for photography'}
                      </button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      <section className="social-photography-section">
        <div className="social-photography-list-heading">
          <h3>Currently with social team ({sentPieces.length})</h3>
          <button type="button" onClick={loadSent} disabled={loadingSent}>{loadingSent ? 'Refreshing…' : 'Refresh'}</button>
        </div>
        {!loadingSent && sentPieces.length === 0 && <p>No pieces are currently out.</p>}
        {sentPieces.length > 0 && (
          <div className="social-photography-table-wrap">
            <table className="inventory-table">
              <thead><tr><th>SKU</th><th>Piece ID</th><th>Length</th><th>Sent at</th><th>Note</th><th>Action</th></tr></thead>
              <tbody>{sentPieces.map((piece) => (
                <tr key={`${piece.sku}_${piece.pieceId}`}>
                  <td>{piece.sku}</td><td>{piece.pieceId}</td><td>{Number(piece.length || 0).toFixed(2)}m</td>
                  <td>{formatDate(piece.socialPhotography?.sentAt)}</td><td>{piece.socialPhotography?.note || '—'}</td>
                  <td>{canManage && <button type="button" disabled={!!busy} onClick={() => changeStatus(piece, 'receive')}>
                    {busy === `${piece.sku}_${piece.pieceId}` ? 'Receiving…' : 'Mark received'}
                  </button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </Modal>
  );
}

export default SocialPhotographyModal;
